import React, { useState, useEffect } from 'react';
import FieldWrapper from '../../../../../common/FieldWrapper';
import { AssessmentField, Coupon, CouponAssessment, RegenerateType } from '../../../../../types/generated.types';
import StarRating from './StarRating';
import { HiOutlineInformationCircle, HiSparkles, HiTrash } from 'react-icons/hi2';
import { useCouponsManageContext } from '../../../../../context/CouponsManageContext';
import { FaArrowRotateLeft, FaArrowRotateRight, FaRegTrashCan } from 'react-icons/fa6';
import AutoResizeTextarea from '../../../../../common/AutoResizeTextarea';
import { ApprovalAction, ButtonVariant } from '../../../../../types/types';
import CouponBottomActions from './CouponBottomActions';
import CouponAssessmentWrapper from './CouponAssessmentWrapper';
import ModalRegenerate from './ModalRegenerate';
import { useConfirmationModalContext } from '../../../../../context/ConfirmationModalContext';
import { Field } from '../../../../../common/Form';


const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

interface CouponAssessmentEditProps {
  assessment?: CouponAssessment | null;
  coupon: any;
  displayCoupon?: any;
  pendingApproval?: any;

  couponId: string;

  isLoadingCoupon: (loading: boolean) => void;
  onChange: (updates: Partial<CouponAssessment>) => void;
  updateContext: (contextUpdate: Partial<Coupon>) => void;
  onBlur: () => void;
}

const CouponAssessmentEdit: React.FC<CouponAssessmentEditProps> = ({
  assessment, coupon, displayCoupon, pendingApproval, couponId, isLoadingCoupon, onChange, updateContext, onBlur: onBlurParent }) => {
  const handleChange = (field: string, value: any) => {
    onChange({ [field]: value });
  };

  const handleContextChange = (value) => {
    updateContext({ ['context']: value });
  }

  const [isRegenerateTypeModalOpen, setIsRegenerateTypeModalOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingType, setRegeneratingType] = useState<RegenerateType | null>(null);

  const [isSelectingFields, setIsSelectingFields] = useState(false);
  const [selectedFields, setSelectedFields] = useState<AssessmentField[]>([]);

  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (couponId.includes('generated')) {
      setIsDirty(true);
    }
  }, [couponId]);

  const {
    undoCoupon,
    redoCoupon,
    canRedoCoupon,
    canUndoCoupon,
    removeCoupon,
    generateCouponSuggestions,
    handleApprovalAction
  } = useCouponsManageContext();

  const confirmationModal = useConfirmationModalContext();

  const onBlur = () => {
    onBlurParent();
    setIsDirty(true);
  }

  const handleGenerateSelectedFields = async () => {
    if (selectedFields.length === 0 || !coupon.companyId) return;

    try {
      setIsRegenerating(true);
      setRegeneratingType(RegenerateType.AssessmentFields);

      await generateCouponSuggestions(
        coupon.companyId,
        1,
        undefined,
        coupon.context,
        couponId,
        RegenerateType.AssessmentFields,
        coupon,
        !!coupon._id,
        selectedFields
      );

      setIsSelectingFields(false);
      setSelectedFields([]);
    } finally {
      setIsRegenerating(false);
      setRegeneratingType(null);
      setIsDirty(true);
    }
  };

  const handleRegenerateSuggestion = async () => {
    if (!pendingApproval) return;

    try {
      setIsRegenerating(true);
      setRegeneratingType(pendingApproval.regenerateType);

      if (pendingApproval.regenerateType === RegenerateType.Coupon) {
        isLoadingCoupon(true);
      }

      await handleApprovalAction(couponId, ApprovalAction.REGENERATE);
    } finally {
      setIsRegenerating(false);
      isLoadingCoupon(false);
      setRegeneratingType(null);
    }
  };

  const handleOpenRegenerateModal = () => {
    setIsRegenerateTypeModalOpen(true);
  };

  const handleRegenerateTypeSelect = async (type: RegenerateType) => {
    if (!coupon.companyId) return;

    try {
      setIsRegenerating(true);
      setRegeneratingType(type);

      if (type === RegenerateType.Coupon) {
        isLoadingCoupon(true);
      }

      await generateCouponSuggestions(
        coupon.companyId,
        1,
        undefined,
        coupon.context,
        couponId,
        type,
        coupon,
        !!coupon._id
      );
    } finally {
      setIsRegenerating(false);
      setRegeneratingType(null);
      isLoadingCoupon(false);
      setIsDirty(true);
    }
  };

  const handleSelectFields = () => {
    setIsSelectingFields(true);
    setSelectedFields([]);
  };

  const handleToggleField = (field: AssessmentField) => {
    setSelectedFields((prev) =>
      prev.includes(field)
        ? prev.filter((f) => f !== field)
        : [...prev, field]
    );
  };

  const handleCancelFieldSelection = () => {
    setIsSelectingFields(false);
    setSelectedFields([]);
  };

  const handleRemoveCoupon = async (couponId: string) => {
    if (isDirty) {
      const r = await confirmationModal.showConfirmation('Vill du ta bort kupongen?', 'Detta går inte att ångra.');

      if (!r) return;
    }

    removeCoupon(couponId);
  };

  const shouldShowAssessmentWrapper = isRegenerating || isSelectingFields || pendingApproval || !assessment?.shouldShowAssessment;

  return (
    <>
      <ModalRegenerate
        isOpen={isRegenerateTypeModalOpen}
        coupon={coupon}
        setContext={handleContextChange}
        onClose={() => setIsRegenerateTypeModalOpen(false)}
        onSelect={handleRegenerateTypeSelect}
        onSelectFields={handleSelectFields}
      />

      <div className="coupon-assessment coupon-assessment-edit flex flex-col">
        <div className='flex items-center justify-between'>
          <div style={{ alignItems: 'last baseline' }} className='flex gap-1'>
            <h4>Betyg</h4>
            <Field
              title=""
              type="switch"
              name="shouldShowAssessment"
              value={assessment?.shouldShowAssessment ?? false}
              onChange={(e) => {
                handleChange('shouldShowAssessment', e['shouldShowAssessment']);
                onBlur();
              }}
            />
          </div>
          {isSelectingFields ? (
            <CouponBottomActions
              actions={[
                {
                  label: 'Avbryt',
                  onClick: handleCancelFieldSelection,
                  variant: ButtonVariant.SECONDARY,
                },
                {
                  label: 'Generera nytt förslag',
                  onClick: handleGenerateSelectedFields,
                  variant: ButtonVariant.PRIMARY,
                  disabled: selectedFields.length === 0,
                },
              ]}
            />
          ) : pendingApproval ? (
            <CouponBottomActions
              actions={[
                {
                  label: 'Avböj',
                  onClick: () => handleApprovalAction(couponId, ApprovalAction.REJECT),
                  variant: ButtonVariant.SECONDARY,
                },
                {
                  label: 'Generera nytt förslag',
                  onClick: handleRegenerateSuggestion,
                  variant: ButtonVariant.SECONDARY,
                },
                {
                  label: 'Godkänn förslag',
                  onClick: () => handleApprovalAction(couponId, ApprovalAction.APPROVE),
                  variant: ButtonVariant.PRIMARY,
                },
              ]}
            />
          ) : (
            <CouponBottomActions
              actions={[
                ...(couponId.includes('coupon') // Only allow deleting unsaved coupons
                  ? [{
                    label: (<FaRegTrashCan />),
                    onClick: () => handleRemoveCoupon(couponId),
                    variant: ButtonVariant.NONE,
                  }]
                  : []),
                {
                  label: (<FaArrowRotateLeft />),
                  onClick: () => undoCoupon(couponId),
                  variant: ButtonVariant.NONE,
                  disabled: !canUndoCoupon(couponId),
                },
                {
                  label: (<FaArrowRotateRight />),
                  onClick: () => redoCoupon(couponId),
                  variant: ButtonVariant.NONE,
                  disabled: !canRedoCoupon(couponId),
                },
                {
                  label: (<><span>Generera med AI</span> <HiSparkles /></>),
                  onClick: handleOpenRegenerateModal,
                  variant: ButtonVariant.SECONDARY,
                  disabled: isRegenerating || pendingApproval || isSelectingFields,
                },
              ]}
            />
          )}
        </div>
        <div className="motivation-bar flex items-center gap-1">
          <HiOutlineInformationCircle />
          <p>Motivering och betyg visas i anslutning till kupongen vid granskningsprocessen.</p>
        </div>
        {shouldShowAssessmentWrapper ? (
          <CouponAssessmentWrapper
            assessment={coupon.assessment || null}
            displayAssessment={displayCoupon.assessment || null}
            isRegenerating={isRegenerating}
            regeneratingType={regeneratingType}
            isSelectingFields={isSelectingFields}
            selectedFields={selectedFields}
            onToggleField={handleToggleField}
            pendingApproval={pendingApproval}
          />
        ) : (
          <main>
            <FieldWrapper name="rating" title="">
              <div className="flex star-rating-container">
                <StarRating rating={assessment?.rating ?? 0} hideText />
                <input
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={assessment?.rating !== undefined && assessment?.rating !== null ? assessment.rating : ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    // make sure that value is a number:
                    if (typeof (value) !== 'number') {
                      return;
                    }

                    handleChange('rating', e.target.value === '' ? null : clamp(value, 0, 5));
                    onBlur();
                  }}
                  onBlur={(e) => {
                    // When field loses focus and is empty, set to 0 for display
                    if (e.target.value === '') {
                      handleChange('rating', 0);
                    }
                  }}
                  placeholder="4.5"
                  style={{ maxWidth: 'max-content' }}
                  className="rating-input"
                  title="Betyg (0-5)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                />
                /5
              </div>

            </FieldWrapper>

            <FieldWrapper name="motivation" title="" x2>
              <input
                type="text"
                value={assessment?.motivation || ''}
                onChange={(e) => handleChange('motivation', e.target.value)}
                onBlur={onBlur}
                placeholder="Motivering sammanfattad i en rubrik med säljande ton för annonsörerna"
                title="Motivering"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
              />
            </FieldWrapper>

            <div className="assessment-columns flex gap-2 mt">
              <FieldWrapper name="expectedEffect" title="FÖRVÄNTAD EFFEKT">
                <AutoResizeTextarea
                  value={assessment?.expectedEffect || ''}
                  onChange={(e) => handleChange('expectedEffect', e.target.value)}
                  onBlur={onBlur}
                  placeholder="AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar"
                  rows={3}
                  title="Förväntad effekt"
                  onPointerDown={(e) => e.stopPropagation()}
                />
              </FieldWrapper>

              <FieldWrapper name="profitability" title="LÖNSAMHET">
                <AutoResizeTextarea
                  value={assessment?.profitability || ''}
                  onChange={(e) => handleChange('profitability', e.target.value)}
                  onBlur={onBlur}
                  placeholder="AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar"
                  rows={3}
                  title="Lönsamhet"
                  onPointerDown={(e) => e.stopPropagation()}
                />
              </FieldWrapper>

              <FieldWrapper name="targetGroupAdaptation" title="MÅLGRUPPSANPASSNING">
                <AutoResizeTextarea
                  value={assessment?.targetGroupAdaptation || ''}
                  onChange={(e) => handleChange('targetGroupAdaptation', e.target.value)}
                  onBlur={onBlur}
                  placeholder="AI-genererad bedömning, ofta låg - hög, smal - bred + kommentar"
                  rows={3}
                  title="Målgruppsanpassning"
                  onPointerDown={(e) => e.stopPropagation()}
                />
              </FieldWrapper>
            </div>
          </main>
        )}
      </div>
    </>
  );
}

export default CouponAssessmentEdit;

